import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Activity, DollarSign, ListChecks, ChevronRight, Target, BarChart3, Calendar, LayoutDashboard } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { KartaSeznam, Dlazdice, EmptyState, SkeletonRows } from "../../components/ui.jsx";
import { useApiData } from "../../lib/api.js";
import { getCurrentUser } from "../../lib/auth.js";
import { fmt, pocetKlientu } from "../../lib/format.js";
import MesicniPriority from "../../components/MesicniPriority.jsx";
import { TaskDetailModal } from "../trener/Ukoly.jsx";
import { NotifikacePrehled } from "../../components/OperationsNotifikace.jsx";

const normName = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();

// Manažerský operativní dashboard — řízení týmu, ne finance.
// Priority měsíce nahoře, KPI, fronta "Vyžaduje pozornost", tým snapshot,
// plnění cílů (read-only) a rychlé akce. Napojeno na /api/data-overview,
// /api/trainers a /api/trener-ukoly.

function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}


function capColor(v) {
  if (v >= 81) return "text-emerald-700";
  if (v >= 70) return "text-green-600";
  if (v >= 50) return "text-amber-600";
  return "text-red-600";
}
function winColor(v) {
  if (v == null) return "text-slate-400";
  if (v >= 50) return "text-emerald-700";
  if (v >= 30) return "text-amber-700";
  return "text-red-700";
}

// 12.9.2026: dlaždice ze vzorníku. Dřív to byla vlastní krabička s barevným
// kroužkem podle nálady; barva teď nese význam a sedí na zbytek aplikace.
const TON_DLAZDICE = { violet: "znacka", indigo: "info", emerald: "ok", amber: "pozor" };
function KpiTile({ icon: Icon, label, value, sub, accent = "violet" }) {
  return (
    <Dlazdice
      popisek={label}
      hodnota={<span className="inline-flex items-center gap-2"><Icon className="h-4 w-4 shrink-0 opacity-70" />{value}</span>}
      ton={TON_DLAZDICE[accent] || "znacka"}
      doplnky={sub != null && sub !== "" ? <span className="text-xs text-slate-500">{sub}</span> : null}
    />
  );
}

// Hezký badge s deadline (po termínu červený, dnes jantarový, jinak neutrální).
function DeadlineBadge({ iso, overdue }) {
  if (!iso) return null;
  const d = String(iso).slice(0, 10);
  const parts = d.split("-");
  const label = parts.length === 3 ? `${Number(parts[2])}. ${Number(parts[1])}.` : d;
  const isToday = d === todayStr();
  const cls = overdue
    ? "bg-red-50 text-red-700 border-red-200"
    : isToday
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-slate-100 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold tabular-nums px-2 py-1 rounded-full border shrink-0 ${cls}`}>
      <Calendar className="w-3 h-3" /> {label}
    </span>
  );
}

function GoalBar({ label, pct, valueText }) {
  const p = Math.max(0, Math.min(100, Number(pct) || 0));
  const done = p >= 100;
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="text-sm font-semibold text-slate-600">{label}</span>
        <span className={`text-sm font-bold tabular-nums ${done ? "text-emerald-700" : "text-slate-800"}`}>
          {valueText}{" "}
          <span className={`ml-1 rounded-lg px-1.5 py-0.5 text-xs font-bold ${done ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{p} %</span>
        </span>
      </div>
      <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${done ? "bg-emerald-500" : "bg-amber-400"}`} style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}


export default function ManagerDashboard() {
  const monthKey = currentMonthKey();
  const { data: ov } = useApiData("/api/data-overview");
  const { data: tr } = useApiData("/api/trainers");
  const { data: tasksData, refetch: refetchTasks } = useApiData("/api/trener-ukoly");
  const { data: settings } = useApiData(`/api/settings?month=${monthKey}`);
  // Moje úkoly z Operations boardu (rychlý přehled, 20.7.2026): otevřené úkoly,
  // kde jsem pilot — seřazené dle deadlinu, po deadlinu červeně.
  const { data: opsUkoly } = useApiData("/api/operations-ukoly");
  const mojeOpsUkoly = useMemo(() => {
    const ja = normName(getCurrentUser()?.name || "");
    if (!ja) return [];
    return ((opsUkoly && opsUkoly.tasks) || [])
      .filter((t) => !["hotovo", "archiv", "zasobnik"].includes(t.status))
      .filter((t) => {
        const p = normName(t.pilot);
        return p && (p === ja || p.includes(ja.split(" ")[0]) || ja.includes(p.split(" ")[0]));
      })
      .sort((a, b) => String(a.deadline || "9999").localeCompare(String(b.deadline || "9999")));
  }, [opsUkoly]);

  const o = ov || {};
  const [detailId, setDetailId] = useState(null);
  const [showAllTasks, setShowAllTasks] = useState(false);
  const mgrRole = getCurrentUser()?.role || "manager";
  const canApprove = ["owner", "manager", "head_coach"].includes(mgrRole);
  const canDelete = ["owner", "manager"].includes(mgrRole);

  // Kapacita per trenér ze stejného zdroje jako Reporting coaches (data-overview breakdown).
  const capByName = useMemo(() => {
    const m = new Map();
    for (const c of (o.capacity && o.capacity.trainerBreakdown) || []) m.set(normName(c.name), c);
    return m;
  }, [o]);

  const trainers = useMemo(() => {
    const list = (tr && tr.trainers) || [];
    return list.filter((t) => t.status === "Aktivní" && t.is_trener !== false)
      .sort((a, b) => (b.activeClientsCount || 0) - (a.activeClientsCount || 0));
  }, [tr]);

  const trenerOptions = useMemo(() => trainers.map((t) => ({ value: t.jmeno, label: t.jmeno })), [trainers]);
  const activeTrainerSet = useMemo(() => new Set(trainers.map((t) => normName(t.jmeno))), [trainers]);

  // Aktivní úkoly (status != hotovo/archiv) — stejná logika jako stránka Úkoly.
  // POZOR: pole je `status`, ne `stav` (předtím se počítaly i hotové/archiv → špatný počet).
  // Jen úkoly aktivních trenérů (jako stránka Úkoly), dokud se trenéři nenačtou neořezáváme.
  const activeTasks = useMemo(() => {
    const list = (tasksData && tasksData.tasks) || [];
    const t0 = todayStr();
    const act = list.filter((t) => {
      const st = String(t.status || "naplanovane");
      if (st === "hotovo" || st === "archiv") return false;
      if (activeTrainerSet.size > 0 && !activeTrainerSet.has(normName(t.pilot || ""))) return false;
      return true;
    }).map((t) => ({ ...t, _overdue: !!(t.deadline && String(t.deadline).slice(0, 10) < t0) }));
    return act.sort((a, b) => {
      if (a._overdue !== b._overdue) return a._overdue ? -1 : 1;
      const ad = a.deadline || "9999-12-31", bd = b.deadline || "9999-12-31";
      return ad < bd ? -1 : ad > bd ? 1 : 0;
    });
  }, [tasksData]);
  const openTasks = activeTasks.length;
  const overdueList = useMemo(() => activeTasks.filter((t) => t._overdue), [activeTasks]);
  const restList = useMemo(() => activeTasks.filter((t) => !t._overdue), [activeTasks]);
  const overdueTasks = overdueList.length;

  // Cíle z /api/settings (data-overview je má 0). Plnění = aktuální / cíl.
  const monthlyGoal = settings?.monthlyGoal || 0;
  const newClientGoal = settings?.newClientGoal || 0;
  const monthlyRev = o.monthlyRevenueLive || 0;
  const newClientRev = o.newClientRevenue || 0;
  const monthlyPct = monthlyGoal > 0 ? Math.round((monthlyRev / monthlyGoal) * 100) : 0;
  const newClientPct = newClientGoal > 0 ? Math.round((newClientRev / newClientGoal) * 100) : 0;

  // 12.9.2026: ruční karta „Notifikace" tady byla navíc. Ukazovala jen úkoly
  // trenérů po termínu, bez akcí, a to samé je o kus výš v kartě Úkoly trenérů.
  // Zůstává jen „Notifikace z úkolů", což je skutečný systém se stejnou logikou
  // jako ownerův zvoneček (odložit, archivovat, vrátit).

  const renderTask = (t) => (
    <button key={t.id} onClick={() => setDetailId(t.id)} className="flex w-full items-start gap-3 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-slate-50">
      <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${t._overdue ? "bg-red-500" : t.priority === "Vysoká" ? "bg-amber-500" : "bg-slate-300"}`} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-800">{t.nazev || "(bez názvu)"}</div>
        {/* 12.9.2026: trenér a klient jako pilulky, dřív to byl text slepený tečkou. */}
        <div className="mt-0.5 flex flex-wrap gap-1 demo-sensitive">
          <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{t.pilot || "bez trenéra"}</span>
          {t.klient_name && <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">{t.klient_name}</span>}
        </div>
      </div>
      <span className="mt-0.5 flex shrink-0 items-center gap-1">
        <DeadlineBadge iso={t.deadline} overdue={t._overdue} />
        <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
      </span>
    </button>
  );

  return (
    <div className="space-y-5">
      <GradientHeader title="Dashboard" icon={LayoutDashboard}
        subtitle="Co se dnes děje v týmu, co potřebuje akci a kam směřují cíle." />

      {/* Priority měsíce — prominentně nahoře */}
      <MesicniPriority mesic={monthKey} highlight />

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile
          icon={Users} accent="violet" label="Aktivní klienti"
          value={o.activeClients != null ? o.activeClients : "—"}
          sub={o.newThisMonth > 0 ? `+ ${o.newThisMonth} tento měsíc` : "napříč týmem"}
        />
        <KpiTile
          icon={DollarSign} accent="emerald" label="Měsíční obrat"
          value={o.monthlyRevenueLive != null ? `${fmt(o.monthlyRevenueLive)} Kč` : "—"}
          sub={o.monthlyGoal > 0 ? `cíl ${fmt(o.monthlyGoal)} Kč, ${o.monthlyGoalPct || 0} %` : "aktuální tempo"}
        />
        <KpiTile
          icon={Activity} accent="indigo" label="Kapacita týmu"
          value={(() => { const u = o.capacity ? o.capacity.avgUtilizationPct : null; return u != null ? <span className={capColor(u)}>{u}%</span> : "—"; })()}
          sub={o.capacity ? `${o.capacity.totalUsedPerWeek}/${o.capacity.totalCapacityHours} h týdně` : "využití rozvrhu"}
        />
        <KpiTile
          icon={ListChecks} accent="amber" label="Otevřené úkoly"
          value={tasksData ? openTasks : "—"}
          sub={overdueTasks > 0 ? `${overdueTasks} po termínu` : "vše v termínu"}
        />
      </div>

      {/* Vyžaduje pozornost + Tým snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* 12.9.2026: karta má tmavou hlavičku jako dlaždice na CEO Přehledu,
            naléhavost nese červený odznak, ne barevný rám. Žlutý odkaz nahradilo
            obyčejné tlačítko dole. */}
        <KartaSeznam
          ikona={ListChecks}
          title="Úkoly trenérů"
          odznak={overdueTasks > 0 ? `${overdueTasks} po termínu` : (tasksData ? openTasks : null)}
          odznakTon={overdueTasks > 0 ? "cervena" : "bila"}
        >
          <div className="p-4">
            {!tasksData && <SkeletonRows rows={3} />}
            {tasksData && activeTasks.length === 0 && (
              <EmptyState icon={ListChecks} title="Žádné otevřené úkoly" hint="Tým má hotovo." />
            )}

            {/* Po termínu — vždy nahoře */}
            {overdueList.length > 0 && (
              <div className="space-y-0.5 mb-1">
                <div className="text-[11px] uppercase tracking-wider text-red-500 font-bold px-2.5 pt-1 pb-0.5">Po termínu ({overdueList.length})</div>
                {overdueList.map(renderTask)}
              </div>
            )}

            {/* Ostatní otevřené — sbalený dropdown, ať je jasné že je toho víc */}
            {restList.length > 0 && (
              <div className="border-t border-slate-100 pt-1">
                {/* Šipka vpravo v rámečku, ať je poznat, že se řádek rozbaluje. */}
                <button
                  onClick={() => setShowAllTasks((s) => !s)}
                  aria-expanded={showAllTasks}
                  className={`flex min-h-[48px] w-full items-center gap-2 rounded-xl border px-3 ${showAllTasks ? "border-brand-200 bg-brand-light" : "border-slate-200 bg-white"}`}
                >
                  <span className={`min-w-0 flex-1 text-left text-sm font-bold ${showAllTasks ? "text-brand-950" : "text-slate-800"}`}>Další otevřené úkoly</span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${showAllTasks ? "bg-white text-brand-950" : "bg-slate-100 text-slate-600"}`}>{restList.length}</span>
                  <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border bg-white ${showAllTasks ? "border-brand-200 text-brand-deep" : "border-slate-200 text-slate-500"}`}>
                    <ChevronRight className={`h-4 w-4 transition-transform ${showAllTasks ? "rotate-90" : ""}`} />
                  </span>
                </button>
                {showAllTasks && (
                  <div className="mt-1 max-h-72 space-y-0.5 overflow-y-auto">
                    {restList.map(renderTask)}
                  </div>
                )}
              </div>
            )}
            <Link to="/manager/treneri/ukoly" className="btn btn-secondary mt-3 w-full">
              <ListChecks className="h-4 w-4" /> Otevřít nástěnku
            </Link>
          </div>
        </KartaSeznam>

        <KartaSeznam ikona={Users} title="Tým" odznak={trainers.length || null}>
          {/* 12.9.2026: hlavička sloupců byla POD tabulkou, takže ji člověk četl,
              až když ji nepotřeboval. Teď je nahoře, ve světlém pruhu skupiny. */}
          {trainers.length > 0 && (
            <div className="flex items-center gap-3 border-b border-slate-200 bg-brand-light px-4 py-2 text-[11px] font-bold uppercase tracking-wide text-brand-950">
              <span className="flex-1 pl-11">trenér</span>
              <span className="w-24 text-right">klienti</span>
              <span className="hidden w-14 text-right sm:block">kapac.</span>
              <span className="hidden w-14 text-right sm:block">win 60d</span>
            </div>
          )}
          <div className="divide-y divide-slate-100 px-4">
            {trainers.length === 0 && <SkeletonRows rows={4} />}
            {trainers.map((t) => (
              <div key={t.jmeno} className="flex items-center gap-3 py-2">
                <span className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-plum-600 text-white text-xs font-bold flex items-center justify-center shrink-0 demo-photo">
                  {t.jmeno.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
                </span>
                <span className="text-sm font-semibold text-slate-800 truncate flex-1 demo-sensitive">{t.jmeno}</span>
                <span className="shrink-0 whitespace-nowrap rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-slate-600">
                  {pocetKlientu(t.activeClientsCount)}
                </span>
                {/* 29.6.2026 feedback #20: na mobilu (390px) 3 fixní sloupce usekávaly jméno —
                    kapacita a win-rate se ukazují až od sm:, na telefonu zůstává počet klientů. */}
                {(() => {
                  const cap = capByName.get(normName(t.jmeno));
                  const u = cap ? cap.utilizationPct : null;
                  return <span className={`hidden sm:block text-sm font-bold tabular-nums w-14 text-right ${u != null ? capColor(u) : "text-slate-300"}`}>{u != null ? `${u}%` : "—"}</span>;
                })()}
                <span className={`hidden sm:block text-sm font-bold tabular-nums w-14 text-right ${winColor(t.winRate60)}`}>{t.winRate60 != null ? `${t.winRate60}%` : "—"}</span>
              </div>
            ))}
          </div>
          <div className="p-4 pt-3">
            <Link to="/manager/treneri/reporting" className="btn btn-secondary w-full">
              <Users className="h-4 w-4" /> Detail týmu
            </Link>
          </div>
        </KartaSeznam>
      </div>

      {/* Cíle měsíce (jen pro čtení). 12.9.2026: vedle bývaly Rychlé akce,
          čtyři odkazy do menu. Smazané, menu je hned vedle. */}
      <div className="grid grid-cols-1 gap-4">
        <KartaSeznam ikona={Target} title="Cíle měsíce" podtitulek="Jen pro čtení, nastavuje owner">
          <div className="space-y-3 p-4">
            <GoalBar label="Měsíční obrat" pct={monthlyPct} valueText={`${fmt(monthlyRev)} / ${fmt(monthlyGoal)} Kč`} />
            <GoalBar label="Revenue nových klientů" pct={newClientPct} valueText={`${fmt(newClientRev)} / ${fmt(newClientGoal)} Kč`} />
            <Link to="/manager/reporting/monthly" className="btn btn-secondary w-full">
              <BarChart3 className="h-4 w-4" /> Celý měsíční report
            </Link>
          </div>
        </KartaSeznam>
      </div>

      {/* Moje úkoly z Operations — rychlý přehled co mám aktuálně na stole. */}
      <KartaSeznam ikona={ListChecks} title="Moje úkoly" odznak={mojeOpsUkoly.length || null}>
        {mojeOpsUkoly.length === 0 ? (
          <EmptyState icon={ListChecks} title="Žádné otevřené úkoly" hint="Máš hotovo." />
        ) : (
          <div className="divide-y divide-slate-50 max-h-80 overflow-y-auto">
            {mojeOpsUkoly.slice(0, 10).map((t) => {
              const dnes = new Date().toISOString().slice(0, 10);
              const poDeadlinu = t.deadline && String(t.deadline).slice(0, 10) < dnes;
              const ST = { prioritni: ["Prioritní", "bg-orange-50 text-orange-700"], mesicni: ["Měsíční", "bg-blue-50 text-blue-700"], tydenni: ["Týdenní", "bg-brand-50 text-brand-700"], v_procesu: ["V procesu", "bg-sky-50 text-sky-700"] };
              const st = ST[t.status] || [t.status, "bg-slate-100 text-slate-600"];
              return (
                <Link key={t.id} to="/operations/ukoly" className="flex items-center gap-2.5 px-4 py-2 hover:bg-slate-50 transition-colors">
                  <span className={`w-2 h-2 rounded-full shrink-0 ${poDeadlinu ? "bg-rose-500" : "bg-amber-400"}`} />
                  <span className="text-sm font-semibold text-slate-800 truncate flex-1 min-w-0">{t.nazev}</span>
                  <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${st[1]}`}>{st[0]}</span>
                  {t.deadline && (
                    <span className={`text-[11px] tabular-nums shrink-0 ${poDeadlinu ? "text-rose-600 font-bold" : "text-slate-400"}`}>
                      do {String(t.deadline).slice(8, 10)}. {Number(String(t.deadline).slice(5, 7))}.
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-slate-200 shrink-0" />
                </Link>
              );
            })}
            {mojeOpsUkoly.length > 10 && (
              <div className="px-4 py-2 text-xs text-slate-400">a dalších {mojeOpsUkoly.length - 10} na boardu</div>
            )}
          </div>
        )}
        <div className="p-4 pt-3">
          <Link to="/operations/ukoly" className="btn btn-secondary w-full">
            <ListChecks className="h-4 w-4" /> Otevřít board
          </Link>
        </div>
      </KartaSeznam>

      {/* Notifikace z úkolů (nové, po deadlinu, ke kontrole) — dole dle zadání. */}
      <NotifikacePrehled />

      {detailId && (
        <TaskDetailModal
          taskId={detailId}
          userRole={mgrRole}
          canApprove={canApprove}
          canDelete={canDelete}
          trenerOptions={trenerOptions}
          onClose={() => { setDetailId(null); refetchTasks(); }}
        />
      )}
    </div>
  );
}
